import { maxScale, minScale, scaleStep } from '@readcloud/data';
import {
  annotationsActions,
  annotationsSelectors,
  applicationActions,
  applicationSelectors,
  authSelectors,
  pdfActions,
  pdfSelectors,
  readerActions,
  readerSelectors,
  useAppDispatch,
} from '@readcloud/state';
import C from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { useComponentSize } from '../../hooks/useComponentSize';
import { getType } from '../../constants';
import { ConnectedEPUBSettings } from '../Modals/ConnectedEPUBSettings';
import { ConnectedPDFSettings } from '../Modals/ConnectedPDFSettings';
import { BottomToolbar } from './BottomToolbar/BottomToolbar';
import { trackEvent } from '@readcloud/common';

interface IConnectedBottomProps {
  className?: string;
  pageSeekClassName?: string;
  pageChangeButtonsClassName?: string;
  containerRef: React.RefObject<HTMLElement>;
}

const ConnectedBottom: React.FunctionComponent<IConnectedBottomProps> = (
  props
) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const appData = useSelector(applicationSelectors.getApplcationState);
  const currentlyDrawing = useSelector(
    annotationsSelectors.getCurrentlyDrawing
  );
  // const user = useSelector(authSelectors.getUser);
  // const documentControl = useSelector(readerSelectors.getDocumentControl);
  const pdfData = useSelector(pdfSelectors.getPDFData);

  const { extras, location, type, temporaryScale, setScale, setScreenOrigin } =
    React.useContext(DocumentWithExtrasContext);
  const dispatch = useAppDispatch();

  const containerSize = useComponentSize(props.containerRef);

  return (
    <BottomToolbar
      documentType={type}
      onDiscardDrawing={() =>
        dispatch(annotationsActions.updateCurrentlyDrawing(undefined))
      }
      isOpen={appData.toolbarsOpen && (type === 'PDF' || type === 'EPUB')}
      currentlyDrawing={currentlyDrawing}
      pageChangeButtonsClassName={props.pageChangeButtonsClassName}
      pageSeekClassName={props.pageSeekClassName}
      className={C(props.className, 'toolbar bottom-toolbar')}
      onToc={
        extras?.outline?.length
          ? () =>
              dispatch(
                applicationActions.updateApplication({
                  tocSidebarOpen: true,
                })
              )
          : undefined
      }
      numLocations={readerData.numPages}
      pageIndex={parseInt(location) ? parseInt(location) : 1}
      nextLocation={() => {
        if (!readerData.lockScroll) {
          trackEvent('Webapp/BottomToolbarNextPageClicked', {
            location,
            bookTitle: readerData.title,
            bookId: readerData.bookId,
            cloudId: readerData.cloudId,
          });
          dispatch(readerActions.updateDocumentControl({ type: 'next' }));
        }
      }}
      previousLocation={() => {
        if (!readerData.lockScroll) {
          trackEvent('Webapp/BottomToolbarPreviousPageClicked', {
            location,
            bookTitle: readerData.title,
            bookId: readerData.bookId,
            cloudId: readerData.cloudId,
          });
          dispatch(readerActions.updateDocumentControl({ type: 'prev' }));
        }
      }}
      onLocationChange={(nextLocation) => {
        if (!readerData.lockScroll) {
          trackEvent('Webapp/BottomToolbarPageSeeked', {
            nextLocation,
            location,
            bookTitle: readerData.title,
            bookId: readerData.bookId,
            cloudId: readerData.cloudId,
          });
          dispatch(
            readerActions.updateDocumentControl({
              type: 'changeLocation',
              payload: { location: nextLocation },
            })
          );
        }
      }}
      outerWidth={containerSize.width}
      initialPageWidth={pdfData.mostCommonWidth * pdfData.baseScale}
      pageViewMode={readerData?.viewState?.pageViewMode || 'OnePage'}
      pageLabels={readerData?.pageLabels}
      loadingPageLabels={readerData?.loadingPageLabels}
      noPageViewMode={false}
      noSlidesViewSwitch={false}
      {...(getType(readerData.fileUrl) === 'PDF'
        ? {
            scale: temporaryScale,
            onScaleChange: (newScale) => {
              trackEvent('Webapp/BottomToolbarZoomSelected', {
                bookTitle: readerData.title,
                bookId: readerData.bookId,
                cloudId: readerData.cloudId,
              });
              setScreenOrigin({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
              });
              setScale(newScale);
            },
            onDecreaseScale: () => {
              trackEvent('Webapp/BottomToolbarZoomOutClicked', {
                bookTitle: readerData.title,
                bookId: readerData.bookId,
                cloudId: readerData.cloudId,
              });
              setScreenOrigin({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
              });
              setScale((s) => Math.max(minScale, s - scaleStep));
            },
            onIncreaseScale: () => {
              trackEvent('Webapp/BottomToolbarZoomInClicked', {
                bookTitle: readerData.title,
                bookId: readerData.bookId,
                cloudId: readerData.cloudId,
              });
              setScreenOrigin({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
              });
              setScale((s) => Math.min(maxScale, s + scaleStep));
            },
            noPageViewMode: true, //pdfconatiner.canShowTwoPages(), //Has been set to always false
            noSlidesViewSwitch: false,
          }
        : {})}
      {...(getType(readerData.fileUrl) === 'EPUB'
        ? {
            SettingsComponent: ConnectedEPUBSettings,
          }
        : getType(readerData.fileUrl) === 'PDF'
        ? {
            onPageViewModeChange: (pageViewMode) =>
              dispatch(pdfActions.updatePDFData({ pageViewMode })),
            slidesView: pdfData.slidesView,
            onToggleSlidesView: () =>
              dispatch(
                pdfActions.updatePDFData({
                  slidesView: !pdfData.slidesView,
                })
              ),
            SettingsComponent: ConnectedPDFSettings,
          }
        : {})}
    />
  );
};

export default ConnectedBottom;
